import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  addProductSerialNumber,
  getLastSerialNumber,
  getProductSerialNumberById,
  validateNonDispatchedSerialNumber,
} from "../../api/productSn.api";

interface ProductSerialNumberData {
  product: string;
  count: number;
  serialNumbers: string[];
}

interface ProductSnState {
  serialNumbers: ProductSerialNumberData[];
  lastSerialNumber: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: ProductSnState = {
  serialNumbers: [],
  lastSerialNumber: null,
  loading: false,
  error: null,
};

export const addProductSn = createAsyncThunk(
  "sn/addProductSn",
  async (
    { productId, serialNumber }: { productId: string; serialNumber: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await addProductSerialNumber(productId, serialNumber);
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to add serial number");
    }
  }
);

export const getProductSn = createAsyncThunk(
  "sn/getProductSn",
  async ({ productIds }: { productIds: string[] }, { rejectWithValue }) => {
    try {
      const response = await getProductSerialNumberById(productIds);
      const data = response;
      const result = Object?.entries(data)?.map(([productId, productData]) => ({
        product: productId,
        count: (productData as { count: number; serialNumbers: string[] })
          .count,
        serialNumbers: (
          productData as { count: number; serialNumbers: string[] }
        ).serialNumbers,
      }));
      return result;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || "Failed to fetch serial numbers"
      );
    }
  }
);

export const fetchLastSerialNumber = createAsyncThunk(
  "sn/fetchLastSerialNumber",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getLastSerialNumber();
      return response || null;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || "Failed to fetch last serial number"
      );
    }
  }
);

export const findProductBySerialNumber = createAsyncThunk(
  "sn/findProductBySerialNumber",
  async (serialNumber: string, { rejectWithValue }) => {
    try {
      const data = await validateNonDispatchedSerialNumber(serialNumber);
      return data; // { productId, productName? }
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          "Serial number not found or already dispatched"
      );
    }
  }
);

export const findMaterialBySerialNumber = createAsyncThunk(
  "sn/findMaterialBySerialNumber",
  async (serialNumber: string, { rejectWithValue }) => {
    const trimmedSerialNumber = serialNumber.trim();
    if (!trimmedSerialNumber) {
      return rejectWithValue("Serial number is required");
    }

    try {
      await validateNonDispatchedSerialNumber(trimmedSerialNumber);
      return rejectWithValue("This serial number belongs to a product");
    } catch (error: any) {
      const message = error?.response?.data?.message;
      if (
        error?.response?.status === 404 &&
        message === "Serial number not found"
      ) {
        return { serialNumber: trimmedSerialNumber };
      }

      return rejectWithValue(
        message || "Serial number has already been dispatched",
      );
    }
  },
);

const productSn = createSlice({
  name: "productSn",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(addProductSn.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addProductSn.fulfilled, (state, action) => {
        state.loading = false;
        state.serialNumbers.push(action.payload);
      })
      .addCase(addProductSn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getProductSn.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProductSn.fulfilled, (state, action) => {
        state.loading = false;

        // Directly store the API response as an object instead of an array
        state.serialNumbers = action.payload;
      })

      .addCase(getProductSn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchLastSerialNumber.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLastSerialNumber.fulfilled, (state, action) => {
        state.loading = false;
        state.lastSerialNumber = action?.payload || null;
      })
      .addCase(fetchLastSerialNumber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(findProductBySerialNumber.pending, (state) => {
        state.loading = true;
      })
      .addCase(findProductBySerialNumber.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(findProductBySerialNumber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default productSn.reducer;
